/**
 * Portfolio Insights page - advisory analysis of one account's portfolio.
 *
 * Reuses the ChatPage account-selection pattern (useAccounts + AccountSelector +
 * auto-select of the first account). Loads the latest stored report on mount and
 * on account change, and lets the user generate a fresh one. The page is advisory
 * only: it has no control that submits, executes, or routes a trade.
 *
 * Requirements: 1.5, 1.6, 2.1, 2.3, 2.4, 2.5, 2.6, 5.1-5.6, 6.6-6.8, 7.1, 7.3, 7.5,
 * 8.3, 8.4, 8.5, 8.8, 10.1-10.3, 12.5, 12.8
 */
import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout';
import { Button, Spinner } from '@/components/ui';
import { AccountSelector } from '@/components/dashboard';
import { LiveActivityStatus } from '@/components/chat/LiveActivityStatus';
import { InsightsReportView } from '@/components/insights/InsightsReportView';
import { useAccounts } from '@/hooks';
import { useInsights } from '@/hooks/useInsights';
import { formatDateTime } from '@/utils/formatters';

export function PortfolioInsightsPage() {
  const { accounts, isLoading: accountsLoading } = useAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const { status, report, activities, error, lastGeneratedAt, loadLatest, generate, retry } =
    useInsights();

  // Auto-select first account on load
  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0 && !accountsLoading) {
      const firstAccount = accounts[0];
      if (firstAccount) {
        setSelectedAccountId(firstAccount.account_id);
      }
    }
  }, [accounts, accountsLoading, selectedAccountId]);

  // Load the stored report on mount and whenever the account changes.
  useEffect(() => {
    void loadLatest(selectedAccountId);
  }, [selectedAccountId, loadLatest]);

  const isGenerating = status === 'generating';
  const hasAccount = selectedAccountId !== null;
  // Generate is offered only for a known account, once the initial load has settled.
  const canGenerate = hasAccount && status !== 'loading' && !isGenerating;
  const showReport = hasAccount && report !== null && status !== 'retrieveError';
  const showEmpty = hasAccount && status === 'empty';
  const showLoading = (accountsLoading && !hasAccount) || (hasAccount && status === 'loading');

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl font-bold text-dark-100">Portfolio Insights</h1>
            <AccountSelector
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelect={setSelectedAccountId}
              isLoading={accountsLoading}
              disabled={isGenerating}
            />
          </div>
          <Button
            variant="primary"
            onClick={() => selectedAccountId && void generate(selectedAccountId)}
            disabled={!canGenerate}
            isLoading={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate insights'}
          </Button>
        </div>

        {/* Operating account */}
        {hasAccount ? (
          <p className="text-sm text-dark-400" data-testid="operating-account">
            Account: <span className="font-medium text-dark-200">{selectedAccountId}</span>
          </p>
        ) : (
          !accountsLoading && (
            <p className="text-sm text-dark-300" data-testid="no-account-message">
              No account selected. Select an account to view or generate portfolio insights.
            </p>
          )
        )}

        {/* Status messages: progress and success */}
        <div aria-live="polite" className="space-y-2">
          {isGenerating && (
            <div className="space-y-1" data-testid="generating-indicator">
              <div className="flex items-center gap-2 text-sm text-dark-300">
                <Spinner size="sm" />
                <span>Generating insights. This can take a few minutes.</span>
              </div>
              <LiveActivityStatus activities={activities} />
            </div>
          )}
          {status === 'shown' && lastGeneratedAt && (
            <p className="text-sm text-emerald-400" data-testid="generate-success">
              Insights generated ({formatDateTime(lastGeneratedAt)}).
            </p>
          )}
        </div>

        {/* Errors */}
        {status === 'genError' && error && (
          <div
            role="alert"
            className="rounded-lg border border-error-700/50 bg-error-900/30 px-4 py-3 text-sm text-error-300"
          >
            {error}
          </div>
        )}
        {hasAccount && status === 'retrieveError' && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-error-700/50 bg-error-900/30 px-4 py-3 text-sm text-error-300"
          >
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={() => void retry()}>
              Retry
            </Button>
          </div>
        )}

        {/* Body */}
        {showLoading && (
          <div className="flex items-center gap-2 text-sm text-dark-400">
            <Spinner size="sm" />
            <span>Loading insights...</span>
          </div>
        )}
        {showEmpty && (
          <p className="text-sm text-dark-400" data-testid="insights-empty">
            No insights have been generated for this account yet. Use Generate insights to create
            the first report.
          </p>
        )}
        {showReport && report && <InsightsReportView report={report} />}
      </div>
    </MainLayout>
  );
}
